import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import TopBackNavBar from '../../components/BackNavBar';
import BodyText from '../../components/BodyText';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import bookfair from '../../interfaces/bookfair-info';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { RedButton } from '../../components/Button';
import useUpdateSessionInfo from '../../utils/updateSessionInfo';
import { Pages } from '../../App';
import { setTaxErrorData } from '../../DumbleData';
import { ConfigContext } from '../../providers/ConfigContext';
import { SaleResponseInfo } from '../../interfaces/sale-response-info';
import useMakeSale from '../../hooks/useMakeSale';
import PaymentPayload from '../../interfaces/payment-payload';

interface Props {
	pInfo: PaymentInfo;
	bfInfo: bookfair;
	setBookfairInfo: any;
	isTaxExemptChecked: boolean;
	setTransactionId: React.Dispatch<React.SetStateAction<string>>;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	setSaleResponseInfo: React.Dispatch<React.SetStateAction<SaleResponseInfo>>;
}

const STFRedeemContent = ({ pInfo, bfInfo, setBookfairInfo, isTaxExemptChecked, setTransactionId, setCurrentPage, setSaleResponseInfo }: Props) => {
	const { apiBase } = useContext(ConfigContext);
	const [makeSale] = useMakeSale();
	const { getSessionInfo } = useUpdateSessionInfo();
	// Following used when processing a request
	const [isButtonProcessing, setIsButtonProcessing] = useState(false);

	useEffect(() => {
		if (pInfo.amountDue.gt(bfInfo.stfBalance)) {
			setStatus(400);
		}
	}, [pInfo.amountDue.toNumber(), bfInfo.stfBalance.toNumber()]);

	const [status, setStatus] = useState<number>(200);

	const handleSTFPayment = async () => {
		setIsButtonProcessing(true);
		const payload: PaymentPayload = {
			total: {
				amount: pInfo.amountDue.toNumber(),
				tax: {
					amount: pInfo.tax.toNumber(),
					rate: bfInfo.taxRate.toNumber(),
					taxableSubtotal: isTaxExemptChecked ? 0 : pInfo.itemTotal.toNumber(),
					status: bfInfo.taxStatus,
					selectedExempt: isTaxExemptChecked,
				},
			},
			tenders: {
				stf: {
					amount: pInfo.amountDue.toNumber(),
				},
			},
		};
		const { data, error } = await makeSale(payload);
		if (error) {
			if (error.status === 401) {
				setCurrentPage(Pages.Landing);
			}
			setIsButtonProcessing(false);
			setStatus(error.status);
			console.error(error);
		} else if (data != null) {
			setTransactionId(data.parentTransactionId);
			setSaleResponseInfo(data);
			window._satellite.track('share-the-fair-payment-submit');
			setCurrentPage(Pages.Confirmation);
		}
	};

	const handleTaxDiscrepancy = async () => {
		const success = await getSessionInfo(apiBase, setBookfairInfo);
		if (success) {
			setCurrentPage(Pages.NewPurchase);
		} else {
			setCurrentPage(Pages.Landing);
		}
	};

	return (
		<>
			<TopBackNavBar setCurrentPage={setCurrentPage} title="Share The Fair Payment" location={Pages.PaymentMethod} />
			<STFRedeemPage>
				<ContentWrapper>
					<CalculationsDiv>
						<RowItemDiv>
							<ItemLabel>Item Total</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.itemTotal.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Tax</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.tax.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Amount Due</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.amountDue.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Available Balance</ItemLabel>
							<ItemValue>{formatCurrencyNumber(bfInfo.stfBalance.toNumber())}</ItemValue>
						</RowItemDiv>
					</CalculationsDiv>
					<StyledRedButton disabled={status !== 200 || isButtonProcessing} handleClick={handleSTFPayment}>
						Submit Payment
					</StyledRedButton>
					{status !== 200 &&
						(status === 422 ? (
							<>
								{setTaxErrorData(true)}
								{window._satellite.track('share-the-fair-payment-tax-discrepancy')}
								<ErrorText>
									There appears to be a discrepancy in the sales tax amount. In order to proceed, please <StyledLink onClick={handleTaxDiscrepancy}>click here</StyledLink> to recalculate the sales
									tax.
								</ErrorText>
							</>
						) : (
							<ErrorText>Total amount due is greater than available balance. Please select a different payment method.</ErrorText>
						))}
				</ContentWrapper>
			</STFRedeemPage>
		</>
	);
};

const STFRedeemPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 280px;
	margin-top: 53px;
`;

const CalculationsDiv = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 38px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;

const ItemLabel = styled(BodyText)`
	display: flex;
	align-items: center;
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	display: flex;
	align-items: center;
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;

const StyledRedButton = styled(RedButton)`
	width: 100%;
	margin: 0;
`;

const ErrorText = styled(BodyText)`
	font-size: 13px;
	margin: 20px 0 0 5px;
	text-align: left;
`;

const StyledLink = styled.a`
	text-decoration: underline;
	cursor: pointer;
`;

export default STFRedeemContent;
